import { createMCPClient, type MCPClient, type MCPClientConfig } from '@ai-sdk/mcp';
import { Experimental_StdioMCPTransport } from '@ai-sdk/mcp/mcp-stdio';
import type { ToolSet } from 'ai';
import type { HarnessConfig } from '../core/types.js';
import { log, getGlobalLogLevel } from '../core/logger.js';

// --- Types ---

/**
 * Tool name filter — applied to the tool map loaded from an MCP server.
 *
 * `include` is allow-list (only named tools kept). `exclude` is block-list
 * (named tools removed). Both work on exact tool names. If both are set,
 * include is applied first then exclude.
 *
 * Motivation: model tool-selection accuracy degrades noticeably past ~20
 * tools. Filesystem servers alone expose 14 tools; adding shell, github,
 * and a browser MCP easily clears 50. Filtering keeps the per-agent
 * interface small without giving up the broad MCP ecosystem.
 */
export interface McpToolFilter {
  include?: string[];
  exclude?: string[];
}

/** Single MCP server configuration (mirrors config schema) */
export interface McpServerConfig {
  transport: 'stdio' | 'http' | 'sse';
  command?: string;
  args?: string[];
  env?: Record<string, string>;
  cwd?: string;
  url?: string;
  headers?: Record<string, string>;
  enabled?: boolean;
  /** Optional tool-name filter. See McpToolFilter. */
  tools?: McpToolFilter;
}

/**
 * npm package identifier for the official filesystem MCP server. When this
 * appears in a stdio server's args and `cwd` is not set, we auto-default
 * cwd to the first path argument so relative paths like '.' and 'tests/'
 * resolve against the allowed directory instead of the process's cwd
 * (which is typically wherever `harness` was invoked from).
 */
const FILESYSTEM_SERVER_PKG = '@modelcontextprotocol/server-filesystem';

/**
 * If a stdio MCP server is the filesystem server and no cwd is set, return
 * the first path argument (if it looks like an absolute path). This matches
 * the pattern `npx -y @modelcontextprotocol/server-filesystem /path/to/dir`.
 *
 * Without this default, calls like `read_text_file({ path: "tests/foo.ts" })`
 * resolve against wherever `harness` was invoked, and the server rejects
 * with "Access denied — path outside allowed directories".
 */
function autoDetectFilesystemCwd(serverConfig: McpServerConfig): string | undefined {
  if (serverConfig.transport !== 'stdio') return undefined;
  if (serverConfig.cwd) return undefined;
  if (!serverConfig.args || serverConfig.args.length === 0) return undefined;

  const fsIdx = serverConfig.args.findIndex((a) => a === FILESYSTEM_SERVER_PKG);
  if (fsIdx < 0) return undefined;

  const pathArg = serverConfig.args[fsIdx + 1];
  if (!pathArg || pathArg.startsWith('-')) return undefined;
  if (!pathArg.startsWith('/') && !pathArg.startsWith('~')) return undefined;

  return pathArg;
}

/**
 * Apply the include/exclude filter to a loaded tool set. Returns a new
 * object with only the allowed tools. Warns (via log) about filter names
 * that don't match any actual tool, which usually means a typo.
 */
function applyToolFilter(
  serverName: string,
  tools: ToolSet,
  filter: McpToolFilter | undefined,
): ToolSet {
  if (!filter) return tools;
  const hasInclude = filter.include && filter.include.length > 0;
  const hasExclude = filter.exclude && filter.exclude.length > 0;
  if (!hasInclude && !hasExclude) return tools;

  const allToolNames = new Set(Object.keys(tools));

  // Surface typos — names in the filter that don't match any loaded tool.
  if (filter.include) {
    for (const name of filter.include) {
      if (!allToolNames.has(name)) {
        log.warn(`MCP "${serverName}": tools.include lists "${name}" but no such tool exists on this server`);
      }
    }
  }
  if (filter.exclude) {
    for (const name of filter.exclude) {
      if (!allToolNames.has(name)) {
        log.warn(`MCP "${serverName}": tools.exclude lists "${name}" but no such tool exists on this server`);
      }
    }
  }

  let result: ToolSet = tools;
  if (hasInclude) {
    const includeSet = new Set(filter.include);
    result = Object.fromEntries(
      Object.entries(result).filter(([name]) => includeSet.has(name)),
    ) as ToolSet;
  }
  if (hasExclude) {
    const excludeSet = new Set(filter.exclude);
    result = Object.fromEntries(
      Object.entries(result).filter(([name]) => !excludeSet.has(name)),
    ) as ToolSet;
  }
  return result;
}

/** Result of connecting to an MCP server */
export interface McpServerConnection {
  name: string;
  client: MCPClient;
  toolCount: number;
  tools: ToolSet;
}

/** Summary of an MCP server for display */
export interface McpServerSummary {
  name: string;
  transport: string;
  enabled: boolean;
  connected: boolean;
  toolCount: number;
  toolNames: string[];
  error?: string;
}

/** Manages all MCP server connections for a harness */
export interface McpManager {
  /** Connect to all enabled MCP servers and load their tools */
  connect(): Promise<void>;
  /** Get merged tool set from all connected servers */
  getTools(): ToolSet;
  /** Get summary of all configured servers */
  getSummaries(): McpServerSummary[];
  /** Close all connected clients */
  close(): Promise<void>;
  /** Check if any servers are configured */
  hasServers(): boolean;
}

// --- Env Var Substitution ---

const ENV_VAR_PATTERN = /\$\{([A-Z_][A-Z0-9_]*)\}/g;

/**
 * Expand `${VAR}` references in a string against `process.env`. Leaves the
 * literal token in place when the var is unset so missing-env errors surface
 * at the MCP server level instead of silently substituting empty strings.
 * Used on MCP `env:` values and `headers:` values so config files can reference
 * secrets without hard-coding them.
 */
function expandEnvVars(value: string): string {
  return value.replace(ENV_VAR_PATTERN, (match, varName: string) => {
    const resolved = process.env[varName];
    return resolved !== undefined ? resolved : match;
  });
}

/** Expand env-var references in every value of a string-to-string record. */
function expandEnvRecord(rec: Record<string, string> | undefined): Record<string, string> | undefined {
  if (!rec) return rec;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec)) {
    out[k] = expandEnvVars(v);
  }
  return out;
}

// --- Transport Factory ---

/**
 * Build an MCPClientConfig from a server config entry.
 * Maps transport type to the appropriate transport configuration.
 */
function buildClientConfig(name: string, serverConfig: McpServerConfig): MCPClientConfig {
  switch (serverConfig.transport) {
    case 'stdio': {
      if (!serverConfig.command) {
        throw new Error(`MCP server "${name}": stdio transport requires "command" field`);
      }
      // Suppress MCP server stderr noise unless --verbose (log level debug)
      const stderr = getGlobalLogLevel() === 'debug' ? 'inherit' as const : 'pipe' as const;

      // Auto-default cwd to the allowed-directory arg when using the
      // filesystem MCP server without an explicit cwd set. This matches
      // the intuitive expectation that bare paths like '.' and 'tests/'
      // resolve to what the server considers its root. See
      // autoDetectFilesystemCwd for the pattern.
      let cwd = serverConfig.cwd;
      if (!cwd) {
        const autoCwd = autoDetectFilesystemCwd(serverConfig);
        if (autoCwd) {
          cwd = autoCwd;
          log.info(`MCP "${name}": auto-defaulted cwd to ${autoCwd} (from filesystem server path arg)`);
        }
      }

      const expandedEnv = expandEnvRecord(serverConfig.env);
      return {
        transport: new Experimental_StdioMCPTransport({
          command: serverConfig.command,
          args: serverConfig.args,
          env: expandedEnv ? { ...process.env, ...expandedEnv } as Record<string, string> : undefined,
          cwd,
          stderr,
        }),
        name: `harness-mcp-${name}`,
      };
    }
    case 'http': {
      if (!serverConfig.url) {
        throw new Error(`MCP server "${name}": http transport requires "url" field`);
      }
      return {
        transport: {
          type: 'http' as const,
          url: serverConfig.url,
          headers: expandEnvRecord(serverConfig.headers),
        },
        name: `harness-mcp-${name}`,
      };
    }
    case 'sse': {
      if (!serverConfig.url) {
        throw new Error(`MCP server "${name}": sse transport requires "url" field`);
      }
      return {
        transport: {
          type: 'sse' as const,
          url: serverConfig.url,
          headers: expandEnvRecord(serverConfig.headers),
        },
        name: `harness-mcp-${name}`,
      };
    }
    default:
      throw new Error(`MCP server "${name}": unknown transport "${serverConfig.transport}"`);
  }
}

// --- Connection ---

/**
 * Connect to a single MCP server and load its tools.
 * Returns the connection with tool set, or throws on failure.
 */
async function connectToServer(name: string, serverConfig: McpServerConfig): Promise<McpServerConnection> {
  const clientConfig = buildClientConfig(name, serverConfig);
  const client = await createMCPClient(clientConfig);

  // Load tools with auto-discovery (no schema pre-definition needed)
  const allTools = await client.tools();
  const originalCount = Object.keys(allTools).length;

  // Apply optional include/exclude filter. Unknown names in the filter
  // produce warnings so typos don't silently drop the wrong tools.
  const tools = applyToolFilter(name, allTools, serverConfig.tools);
  const toolCount = Object.keys(tools).length;

  if (toolCount < originalCount) {
    log.info(`MCP "${name}": tool filter applied, ${toolCount}/${originalCount} tool(s) active`);
  }

  return { name, client, toolCount, tools };
}

// --- Manager ---

/**
 * Create an MCP manager from harness config.
 * Manages lifecycle of all MCP server connections.
 */
export function createMcpManager(config: HarnessConfig): McpManager {
  const servers = config.mcp?.servers ?? {};
  const connections: McpServerConnection[] = [];
  const summaries: McpServerSummary[] = [];

  return {
    hasServers(): boolean {
      return Object.keys(servers).length > 0;
    },

    async connect(): Promise<void> {
      const entries = Object.entries(servers);

      if (entries.length === 0) {
        return;
      }

      log.info(`Connecting to ${entries.length} MCP server(s)...`);

      for (const [name, serverConfig] of entries) {
        const enabled = serverConfig.enabled !== false;

        if (!enabled) {
          summaries.push({
            name,
            transport: serverConfig.transport,
            enabled: false,
            connected: false,
            toolCount: 0,
            toolNames: [],
          });
          log.debug(`MCP server "${name}" is disabled, skipping`);
          continue;
        }

        try {
          const connection = await connectToServer(name, serverConfig);
          connections.push(connection);

          const toolNames = Object.keys(connection.tools);
          summaries.push({
            name,
            transport: serverConfig.transport,
            enabled: true,
            connected: true,
            toolCount: connection.toolCount,
            toolNames,
          });

          log.info(`MCP "${name}": connected, ${connection.toolCount} tool(s) [${toolNames.join(', ')}]`);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          summaries.push({
            name,
            transport: serverConfig.transport,
            enabled: true,
            connected: false,
            toolCount: 0,
            toolNames: [],
            error: message,
          });

          log.warn(`MCP "${name}": connection failed — ${message}`);
        }
      }

      const totalTools = connections.reduce((sum, c) => sum + c.toolCount, 0);
      if (totalTools > 0) {
        log.info(`MCP: ${totalTools} tool(s) loaded from ${connections.length} server(s)`);
      }
    },

    getTools(): ToolSet {
      const merged: ToolSet = {};
      for (const connection of connections) {
        Object.assign(merged, connection.tools);
      }
      return merged;
    },

    getSummaries(): McpServerSummary[] {
      // Include unchecked servers that haven't been connected yet
      const serverNames = Object.keys(servers);
      const checkedNames = new Set(summaries.map((s) => s.name));

      for (const name of serverNames) {
        if (!checkedNames.has(name)) {
          const serverConfig = servers[name];
          summaries.push({
            name,
            transport: serverConfig.transport,
            enabled: serverConfig.enabled !== false,
            connected: false,
            toolCount: 0,
            toolNames: [],
          });
        }
      }

      return [...summaries];
    },

    async close(): Promise<void> {
      const closePromises = connections.map(async (connection) => {
        try {
          await connection.client.close();
          log.debug(`MCP "${connection.name}": closed`);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          log.warn(`MCP "${connection.name}": close failed — ${message}`);
        }
      });

      await Promise.all(closePromises);
      connections.length = 0;
    },
  };
}

/**
 * Load MCP tools from all enabled servers in config.
 * Convenience function for one-shot usage (connects, loads, returns tools + close fn).
 */
export async function loadMcpTools(config: HarnessConfig): Promise<{
  tools: ToolSet;
  summaries: McpServerSummary[];
  close: () => Promise<void>;
}> {
  const manager = createMcpManager(config);
  await manager.connect();

  return {
    tools: manager.getTools(),
    summaries: manager.getSummaries(),
    close: () => manager.close(),
  };
}

/**
 * Validate MCP server configurations without connecting.
 * Returns validation errors for each server.
 */
export function validateMcpConfig(config: HarnessConfig): Array<{ server: string; error: string }> {
  const errors: Array<{ server: string; error: string }> = [];
  const servers = config.mcp?.servers ?? {};

  for (const [name, serverConfig] of Object.entries(servers)) {
    if (serverConfig.transport === 'stdio') {
      if (!serverConfig.command) {
        errors.push({ server: name, error: 'stdio transport requires "command" field' });
      }
    } else if (serverConfig.transport === 'http' || serverConfig.transport === 'sse') {
      if (!serverConfig.url) {
        errors.push({ server: name, error: `${serverConfig.transport} transport requires "url" field` });
      }
    } else {
      errors.push({ server: name, error: `unknown transport "${serverConfig.transport}"` });
    }
  }

  return errors;
}
